// G3 — the original-pane selection layer (non-English books only; mounted by the
// reader solely when `sourceLanguage !== "en"`). It listens for a text selection
// inside the original pane's subtree, snaps it to whole tokens against the
// original `DocumentModel` (convex/model/selection), and floats the gloss
// popover at the selection rect. Ported from lekterion's `SelectionLayer` +
// `dom-offsets`, stripped to the three-tab gloss surface (no copy/correction/
// read-aloud/locus menu).
//
// The retelling pane never mounts this, and Peter Pan (English) never does
// either — the reader gates on `sourceLanguage`, so the popover is structurally
// impossible outside a foreign original.
//
// THREE ways in, ONE capture. Mouse and touch are the pointer paths below;
// word navigation is `./use-gloss-keyboard`, which paints a real DOM selection
// from a token cursor and then calls the very same `capture`; caret browsing
// (F7, AT) brings its own selection and only needs noticing. Every gloss,
// however it was reached, is the same `NormalizedSpan` against the same cache
// key. `via` records which, and is consulted for exactly two decisions —
// whether focus belongs in the popover while it is open, and what gets it back
// when the popover closes (see `CaptureVia` and `endCapture`).
//
// The keyboard additions deliberately touch NONE of the pointer state machine
// (`ownsPress`, the `onInteractOutside` suppression, per-touch
// `selectionchange` tracking, capture-phase scroll dismissal, touch-scroll
// selection clearing). They can't: keyboard input generates no pointer events,
// so every one of those handlers' entry conditions — a `mouseup`, a
// `touchstart`, a live `touchInProgress` — is unreachable from a keystroke.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@bindery/ui/components/ui/popover";
import { flushPerformanceChunk, useLogger } from "@/logging";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import {
  containingBlocks,
  normalizeSelection,
  spanTokenCount,
  type NormalizedSpan,
} from "@bindery/convex-retells/model/selection";
import {
  offsetAtPoint,
  ORIGINAL_SCOPE,
  selectionToOffsets,
  selectOffsets,
} from "@bindery/reader-dom/dom-offsets";
import { GlossPopover } from "./gloss-popover";
import { useGlossKeyboard } from "./use-gloss-keyboard";

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Which input method produced a capture. All three arrive at `capture` holding
 * a real DOM selection and leave it holding the same `NormalizedSpan`; they
 * differ only in what the popover owes their focus.
 *
 * - `pointer` — mouse or touch. Focus never left the reader's control, so the
 *   popover neither takes it on open nor returns it on close.
 * - `keyboard` — word-navigation mode. Focus is parked on the invisible sink,
 *   so the popover TAKES it (that is what turns a 58-Tab trek to the gate
 *   chips into no Tabs at all) and hands it back to the sink on close.
 * - `caret` — Firefox's F7 caret browsing, and any AT driving a real DOM
 *   selection. There is no sink and no cursor: the reader's caret IS their
 *   place, and pulling focus into the popover would lose it. Focus stays put;
 *   only a reader who Tabs into the popover themselves gets it returned.
 */
type CaptureVia = "pointer" | "keyboard" | "caret";

interface Captured {
  rect: Rect;
  norm: NormalizedSpan;
  paragraph: string;
  via: CaptureVia;
  /**
   * Who held focus when the capture was taken — the landing spot for a `caret`
   * capture whose reader Tabbed into the popover. Radix's own close-focus
   * return can't be relied on for it: two of the three close paths unmount
   * this content from the PARENT, and the one dispatch that does survive that
   * (`onCloseAutoFocus`, from a detached-container timeout) arrives a beat
   * late, after `endCapture` has already placed focus.
   */
  returnFocus: HTMLElement | null;
}

function inPopover(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(
      '[data-slot="popover-content"], [data-radix-popper-content-wrapper]',
    ) !== null
  );
}

/**
 * Presses this layer takes responsibility for: original text, but not a link
 * or button sitting inside it. `onInteractOutside` suppresses Radix's own
 * dismissal for exactly these, so `onTouchEnd` must retarget or dismiss every
 * one of them — the two predicates are the same function so they cannot drift
 * apart and leave a press that nothing dismisses.
 */
function ownsPress(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(ORIGINAL_SCOPE) !== null &&
    target.closest("a, button") === null
  );
}

export function SelectionLayer({
  model,
  rootRef,
  bookSlug,
  chapterNumber,
  sourceLanguage,
  action,
}: {
  model: DocumentModel;
  /**
   * The scroll container the original's `[data-start]` runs live in — the
   * original pane itself on desktop, the shared interleaved column on mobile.
   * Either way only its `[data-pane="original"]` subtrees are resolved.
   */
  rootRef: RefObject<HTMLDivElement | null>;
  bookSlug: string;
  chapterNumber: number;
  sourceLanguage: string;
  /**
   * An extra control for the gloss card, rendered from the captured span. The
   * READER PASSES NOTHING — this exists for the admin workbench, whose "Set
   * anchor" button wants the very number `normalizeSelection` already computed
   * (`src/admin/anchor-picking.ts`, Path 1). Omitted, reader behaviour is
   * unchanged by construction.
   *
   * A render prop rather than `children`, because the parent needs the span
   * back (composition policy §3.2's stated exception). It is used in RENDER
   * only, and must never be added to the listener effect's dependency array
   * below: an inline arrow is a new identity every render, and would tear down
   * and re-register all seven document listeners each time.
   */
  action?: (norm: NormalizedSpan) => ReactNode;
}) {
  const [captured, setCaptured] = useState<Captured | null>(null);
  const log = useLogger();
  const logRef = useRef(log);
  // Mirror of `captured` for the document-level touch handlers: the listener
  // effect deliberately doesn't re-run on capture changes, so its closure
  // can't read the state directly.
  const capturedRef = useRef(captured);

  // The keyboard mode's two `sr-only` surfaces. Both are rendered
  // unconditionally, below — the sink has to exist before the first capture,
  // and a live region only speaks if it was already in the DOM when its text
  // changed.
  const sinkRef = useRef<HTMLSpanElement | null>(null);
  const announceRef = useRef<HTMLOutputElement | null>(null);

  // The listener effect below owns `scheduleCapture`, which the keyboard hook
  // has to be able to invoke; a ref keeps `onInvoke` referentially stable so
  // the hook's own effect doesn't churn on every render.
  const captureRef = useRef<
    (via: CaptureVia, done?: (captured: boolean) => void) => void
  >(() => undefined);
  const onInvoke = useCallback((done: (captured: boolean) => void) => {
    captureRef.current("keyboard", done);
  }, []);

  const { modeActive, restore, exit } = useGlossKeyboard({
    rootRef,
    model,
    onInvoke,
    popoverOpen: captured !== null,
    sinkRef,
    announceRef,
  });
  const modeActiveRef = useRef(modeActive);

  useEffect(() => {
    logRef.current = log;
    capturedRef.current = captured;
    modeActiveRef.current = modeActive;
  });

  /**
   * THE close invariant: a capture hands the keyboard its focus back EXACTLY
   * ONCE, done by the first of the close paths to observe that capture ending,
   * and never by anyone else. `restoredRef` is what enforces it — `Captured`
   * objects are per-capture, so identity is the whole latch.
   *
   * There are more close paths than there look to be, and two of them fire on
   * a single Escape:
   *
   *  1. `onOpenChange(false)` — Radix's own dismissal.
   *  2. `onOpenChange(false)` AGAIN, re-entered from inside step 1: `restore()`
   *     focuses the sink, the sink renders outside `[data-pane="original"]`,
   *     and Radix routes focus-outside through `onInteractOutside` (a
   *     `FocusEvent`, which `ownsPress` can't and shouldn't suppress) into a
   *     second dismissal.
   *  3. The capture-phase scroll dismissal, which unmounts from the parent.
   *  4. `onCloseAutoFocus`, which DOES fire — the focus scope's cleanup
   *     dispatches it from a `setTimeout(…, 0)` on the already-detached
   *     container, so unmounting from the parent does not stop it.
   *
   * Unlatched, an Escape ran `restore()` three times: two full `paint()`
   * sweeps of the pane each, and — because `announce` alternates a trailing
   * space so a repeat still reads as a change — the word spoken three extra
   * times on every close.
   */
  const restoredRef = useRef<Captured | null>(null);
  const endCapture = useCallback(
    (ended: Captured, byPointer: boolean): void => {
      if (ended.via === "pointer") return;
      if (restoredRef.current === ended) return;
      restoredRef.current = ended;
      if (byPointer) {
        // The reader has reached for the mouse and clicked somewhere else.
        // Repainting the cursor and yanking focus to the sink would fight the
        // click that is still in flight, so word navigation simply ends where
        // they left it — without the deliberate-exit refocus, which would be
        // the same theft by another name.
        if (ended.via === "keyboard") exit(false);
        return;
      }
      if (ended.via === "keyboard") restore();
      else if (ended.returnFocus?.isConnected) {
        ended.returnFocus.focus({ preventScroll: true });
      }
    },
    [restore, exit],
  );

  /**
   * Set by `onInteractOutside` just before Radix dismisses, and consumed by the
   * `onOpenChange` it causes (Radix calls them in that order, synchronously,
   * and dismisses only when our handler left the event unprevented).
   */
  const pointerDismissRef = useRef(false);

  useEffect(() => {
    // Read `rootRef.current` live in each handler (not captured here) so a
    // desktop-pane ↔ mobile-column swap, which reuses the same ref for a new
    // node, keeps working without re-running this effect.
    const clear = (): void => {
      setCaptured(null);
    };

    // Scheduled via rAF from three handlers below; track the pending id so
    // cleanup can cancel it — otherwise a frame queued just before a
    // chapter/model change effect re-run fires `capture` against the stale
    // closure's `model` one frame late.
    let rafId: number | null = null;
    const scheduleCapture = (
      via: CaptureVia,
      done?: (captured: boolean) => void,
    ): void => {
      rafId = window.requestAnimationFrame(() => {
        capture(via, done);
      });
    };
    captureRef.current = scheduleCapture;

    const capture = (
      via: CaptureVia,
      done?: (captured: boolean) => void,
    ): void => {
      // Every bail reports back: word-navigation mode suspends its own
      // blur-driven exit while a capture is in flight, so an invocation that
      // takes nothing has to say so or the mode is stuck (see `onInvoke`).
      const nothing = (): void => {
        done?.(false);
        clear();
      };
      const root = rootRef.current;
      if (!root) return nothing();
      const sel = window.getSelection();
      const offsets = selectionToOffsets(root, sel, ORIGINAL_SCOPE);
      if (!offsets || !sel) return nothing();
      const norm = normalizeSelection(model, offsets.start, offsets.end);
      if (!norm) return nothing();
      done?.(true);
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setCaptured({
        rect: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
        norm,
        paragraph: containingBlocks(model, norm),
        via,
        returnFocus:
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null,
      });
      logRef.current.event("gloss.open", {
        book: bookSlug,
        chapter: chapterNumber,
        tokens: spanTokenCount(model, norm.start, norm.end),
        quoteLength: norm.quote.length,
        // Which input method reached the gloss — the measure of whether the
        // keyboard path is actually used. A `data` key, so it costs no field
        // slot (logging policy §12).
        via,
      });
      // Bound the long-task chunk here: selecting a span is the reader's
      // densest interaction, so "how much did the main thread block in the
      // run-up to this gloss" is the question worth answering.
      flushPerformanceChunk("gloss_open", {
        book: bookSlug,
        chapter: chapterNumber,
      });
    };

    const onMouseUp = (e: MouseEvent): void => {
      if (e.button !== 0 || inPopover(e.target)) return;
      scheduleCapture("pointer");
    };

    /**
     * The caret-browsing complement (Firefox's F7, and any AT that drives a
     * real DOM selection). These readers never enter word-navigation mode —
     * they already have a caret and a shift-arrow — so all that is missing is
     * someone to notice the selection they just made. Deliberately on `keyup`,
     * not `keydown`: the selection only exists after the browser has applied
     * the key.
     */
    const onKeyUp = (e: KeyboardEvent): void => {
      // The same exclusion `onMouseUp` makes, for the same reason: the
      // popover's own `TabsList` is a Radix roving-focus group driven with
      // arrow keys, so a gloss opened BY MOUSE and then tabbed through would
      // otherwise re-capture the still-live pointer selection on every arrow
      // press — a duplicate `gloss.open` per press, and the capture's `via`
      // flipped away from "pointer", inflating the very metric below.
      if (inPopover(e.target)) return;
      if (modeActiveRef.current) return;
      if (e.key !== "Shift" && !e.key.startsWith("Arrow")) return;
      const root = rootRef.current;
      if (!root) return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      if (!selectionToOffsets(root, sel, ORIGINAL_SCOPE)) return;
      scheduleCapture("caret");
    };

    // Touch tap-to-word: a tap (no drag, no existing selection) selects the
    // whole token under the point, so mobile readers get the same popover.
    const TAP_SLOP = 10;
    let tapAnchor: { x: number; y: number } | null = null;
    let tapMoved = false;
    // True while any finger is on the page — the signal `onScroll` uses to
    // tell a finger-pan of the column from a mouse/trackpad scroll. Fingers on
    // the native selection handles never reach the page (they are browser UI),
    // so a handle drag that auto-scrolls at the viewport edge does not count.
    let touchInProgress = false;
    // Did THIS touch create or change the DOM selection (a long-press word
    // select, a handle drag)? Only then may `onTouchEnd` capture the live
    // selection — a selection that predates the touch is the PREVIOUS
    // popover's, milliseconds from being cleared by its dismissal, and
    // capturing it pins the old popover in place instead of letting the tap
    // retarget to the word under the finger.
    let selectionChangedDuringTouch = false;
    const onSelectionChange = (): void => {
      if (touchInProgress) selectionChangedDuringTouch = true;
    };
    const onTouchStart = (e: TouchEvent): void => {
      touchInProgress = true;
      selectionChangedDuringTouch = false;
      const t = e.touches.length === 1 ? e.touches[0] : undefined;
      tapAnchor = t ? { x: t.clientX, y: t.clientY } : null;
      tapMoved = false;
    };
    const onTouchMove = (e: TouchEvent): void => {
      const t = e.touches[0];
      if (!tapAnchor || !t) return;
      if (
        Math.hypot(t.clientX - tapAnchor.x, t.clientY - tapAnchor.y) > TAP_SLOP
      ) {
        tapMoved = true;
      }
    };
    // An open popover goes away, and the stale quote selection with it — but a
    // selection THIS touch just made (a long-press in the prose) is the
    // reader's, not ours, and survives.
    const dismiss = (): void => {
      if (capturedRef.current === null) return;
      if (!selectionChangedDuringTouch) {
        window.getSelection()?.removeAllRanges();
      }
      clear();
    };

    /** The whole word under a completed tap, or null if it can't retarget. */
    const wordUnder = (
      root: HTMLElement,
      point: Touch,
    ): NormalizedSpan | null => {
      const hit = document.elementFromPoint(point.clientX, point.clientY);
      if (!hit || !root.contains(hit) || hit.closest("a, button")) return null;
      const offset = offsetAtPoint(
        root,
        point.clientX,
        point.clientY,
        ORIGINAL_SCOPE,
      );
      return offset === null ? null : normalizeSelection(model, offset, offset);
    };

    const onTouchEnd = (e: TouchEvent): void => {
      if (e.touches.length === 0) touchInProgress = false;
      const anchor = tapAnchor;
      tapAnchor = null;
      const root = rootRef.current;
      if (!root || inPopover(e.target)) return;
      const sel = window.getSelection();
      if (
        sel &&
        !sel.isCollapsed &&
        selectionChangedDuringTouch &&
        selectionToOffsets(root, sel, ORIGINAL_SCOPE)
      ) {
        scheduleCapture("pointer");
        return;
      }
      const point = e.changedTouches[0];
      // `tapMoved` is a >10px drag that produced no scroll; a null `anchor` is
      // a multi-finger touchstart (a pinch-zoom). Neither can retarget.
      const norm =
        tapMoved || !anchor || !point ? null : wordUnder(root, point);
      if (norm && selectOffsets(root, norm.start, norm.end, ORIGINAL_SCOPE)) {
        e.preventDefault(); // suppress synthetic mouse events + native callout
        scheduleCapture("pointer");
        return;
      }
      // Nothing to retarget to. Because `onInteractOutside` below suppressed
      // Radix's dismissal for this press, dismissing is now ours to do — and
      // every path out of here has to do it, or the popover stays pinned over
      // stale text with no scroll event coming to close it.
      if (ownsPress(e.target)) dismiss();
    };

    // iOS/Android can cancel a touch (system gesture, browser taking over the
    // native selection UI) without ever firing touchend — reset both trackers
    // or a stale `touchInProgress` would misclassify later wheel scrolls.
    const onTouchCancel = (e: TouchEvent): void => {
      if (e.touches.length === 0) touchInProgress = false;
      tapAnchor = null;
    };

    const onScroll = (): void => {
      // The anchor rect is viewport-fixed; a scroll invalidates it, so close.
      // On a finger PAN the still-live DOM selection must go too: `onTouchEnd`'s
      // recapture branch reads it at lift, so leaving it in place resurrects
      // the popover the scroll just closed — anchored at the word's new
      // (possibly offscreen) rect, chasing the reader down the column.
      //
      // But a finger extending a selection past the viewport edge auto-scrolls
      // while it is still down, and clearing there makes a span longer than one
      // screen impossible to select. `selectionChangedDuringTouch` is the
      // discriminator this state machine already keeps: a pan never touches the
      // selection, a drag that extends one does nothing but. Mouse/trackpad
      // scrolls fire no touch events, so the desktop flow is untouched either
      // way.
      if (touchInProgress && !selectionChangedDuringTouch) {
        window.getSelection()?.removeAllRanges();
      }
      // This path unmounts the popover WITHOUT going through Radix's own close
      // (there is no `onOpenChange` here either), which for a keyboard capture
      // means focus is about to fall to `<body>` with the reader's place lost.
      // Handled here, at the state transition itself, rather than from an
      // effect watching `captured` — the transition IS the event, and an effect
      // would also fire for every OTHER way a capture ends.
      const ended = capturedRef.current;
      setCaptured((c) => (c === null ? c : null));
      if (ended) endCapture(ended, false);
    };

    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: false });
    document.addEventListener("touchcancel", onTouchCancel, { passive: true });
    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });
    return () => {
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchCancel);
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("scroll", onScroll, { capture: true });
      captureRef.current = () => undefined;
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [model, rootRef, bookSlug, chapterNumber, endCapture]);

  return (
    <>
      {/* The three keyboard surfaces, always mounted: the sink must exist
          before the first capture, and a live region only speaks if it was
          already in the DOM when its text changed.

          `role="application"` sits HERE and nowhere else. It suspends a screen
          reader's browse mode so the arrow keys reach our handler instead of
          moving the virtual cursor — which is exactly right for an empty
          one-purpose sink, and would be destructive on the text itself. */}
      <span
        ref={sinkRef}
        tabIndex={-1}
        role="application"
        aria-label="Word navigation. Left and right arrows move by word, up and down by line, shift with an arrow extends the selection, Enter translates, Escape exits."
        className="sr-only"
      />
      <output ref={announceRef} aria-live="polite" className="sr-only" />
      {/* What the entry regions point their `aria-describedby` at. `hidden`
          rather than `sr-only`: a described-by target is read from wherever it
          is, and this one has no business being reachable on its own. */}
      <span id="gloss-kbd-hint" hidden>
        Press Enter to read this text word by word and translate a word or
        phrase.
      </span>
      {captured && (
        <Popover
          open
          onOpenChange={(open) => {
            if (!open) {
              const byPointer = pointerDismissRef.current;
              pointerDismissRef.current = false;
              // The keyboard paths keep their selection: it IS the reader's
              // cursor (or their caret), and it is what they come back to. The
              // pointer path has no cursor to keep, so the highlight goes.
              if (captured.via === "pointer") {
                window.getSelection()?.removeAllRanges();
              } else {
                endCapture(captured, byPointer);
              }
              setCaptured(null);
            }
          }}
        >
          <PopoverAnchor asChild>
            <div
              aria-hidden
              style={{
                position: "fixed",
                left: captured.rect.left,
                top: captured.rect.top,
                width: captured.rect.width,
                height: captured.rect.height,
                pointerEvents: "none",
              }}
            />
          </PopoverAnchor>
          <PopoverContent
            side="top"
            align="center"
            sideOffset={8}
            collisionPadding={16}
            aria-label="Translation"
            onOpenAutoFocus={(e) => {
              // ONLY word-navigation mode wants focus in here. It is parked on
              // an invisible sink with nothing else to do, and letting Radix
              // pull it into the content is precisely what turns a 58-Tab trek
              // to the gate chips into no Tabs at all. The other two paths
              // already have somewhere better: a pointer reader has their own
              // focus, and a caret-browsing reader has a CARET, which taking
              // focus would lose (`onCloseAutoFocus` below can only put focus
              // back on an element, never back in the text).
              if (captured.via !== "keyboard") e.preventDefault();
            }}
            onCloseAutoFocus={(e) => {
              // This DOES fire, on both close paths, including the scroll
              // dismissal that unmounts this content from the parent: the
              // focus scope's cleanup dispatches `autoFocusOnUnmount` from a
              // `setTimeout(…, 0)` on the already-detached container, ungated
              // on `trapped`. (Verified against @radix-ui/react-focus-scope
              // 1.1.12 + react-popover 1.1.19.)
              //
              // So it arrives ~4ms AFTER `endCapture` has already put focus
              // where it belongs, and all that is left for it to do is stop
              // Radix's own focus return from moving it again. It restores
              // nothing — see the close invariant on `endCapture`, which this
              // handler used to violate three ways at once.
              if (captured.via !== "pointer") e.preventDefault();
            }}
            onInteractOutside={(e) => {
              // A press landing in original text is this layer's own retarget/
              // dismiss flow (`onMouseUp`/`onTouchEnd`): Radix must not race it —
              // its dismissal clears the DOM selection between our press and the
              // rAF'd capture, so whether the new popover survived was a coin
              // flip. Presses anywhere else (retold prose, chrome, the inset
              // trigger button) keep Radix's ordinary outside-press dismissal.
              //
              // PRIMARY presses only: `onMouseUp` ignores every other button, so
              // suppressing a right-click or a two-finger tap here would leave the
              // popover open with nothing left to close it.
              const original = e.detail.originalEvent;
              const primary = !(original instanceof MouseEvent)
                ? true
                : original.button === 0;
              if (primary && ownsPress(e.target)) {
                e.preventDefault();
                return;
              }
              // Unprevented, so Radix is about to dismiss. Record whether a
              // POINTER did it: this same handler also runs for focus-outside
              // (`FocusEvent`), which is how `restore()` re-enters the close
              // path from inside itself. `PointerEvent extends MouseEvent`, so
              // the one test covers mouse and touch both.
              pointerDismissRef.current = original instanceof MouseEvent;
            }}
            className="w-80 max-w-[calc(100vw-2rem)]"
          >
            <GlossPopover
              key={`${String(captured.norm.start)}-${String(captured.norm.end)}`}
              model={model}
              norm={captured.norm}
              paragraph={captured.paragraph}
              bookSlug={bookSlug}
              chapterNumber={chapterNumber}
              sourceLanguage={sourceLanguage}
              ownsFocus={captured.via === "keyboard"}
              action={action}
            />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
